import { sentiment_frontier } from '@op/components/Snippets/constants';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getKeyOpinionHighGameGPTReport,
  getSummaryHotEventList,
  getKeyOpinionsRisingKeyword,
  getKeyOpinionsRisingTopic,
} from '@op/services';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRationPercentValue, getTimeSpaceArray } from '@op/utils';
import { dateTypeMap } from '@op/constants';
import moment from 'moment';
import _ from 'lodash-es';
import { objToUrl } from '@op/utils/url';
import { percentageFormat, reviewsFormat } from '@op/utils/value';
import { formatDateFunc, getFilterDateQOQText } from '@op/utils/time';
import {
  useDrilldownParams,
  EDrilldownMode,
} from '@op/hooks/useDrilldownParams';

export function useKeyOpinionsHotEventList({
  filter,
  formatData,
}: {
  filter: any;
  formatData?: any;
}) {
  const [records, setRecords] = useState([]);
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  // const memoizedFilter = useDeepCompareMemoize(filter);

  // function useDeepCompareMemoize(value) {
  //   const ref = useRef();

  //   if (!_.isEqual(value, ref.current)) {
  //     ref.current = value;
  //   }

  //   return ref.current;
  // }

  function requestHotEvent(params = filter) {
    if (!params?.edition_unified_id) return;
    setError(false);
    wrapperRequest((cancelFunc) =>
      getSummaryHotEventList(params, cancelFunc)
        .then((data: any) => {
          const formatRecords = formatData?.(data, params) || data;
          setRecords(formatRecords);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestHotEvent();
  }, [filter]);

  return { data: records, loading, error, refetch: requestHotEvent };
}

export function useKeyOpinionsRisingTopic({
  filter,
  extraData,
}: {
  filter: any;
  extraData?: any;
}) {
  const [records, setRecords] = useState([]);
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  function requestTopic() {
    if (!filter?.edition_unified_id) return;
    const urlParams =
      Object.keys(extraData || {}).length !== 0 ? objToUrl(extraData) : '';
    setError(false);
    setRecords([]);
    wrapperRequest((cancelFunc) =>
      getKeyOpinionsRisingTopic(filter, cancelFunc, urlParams)
        .then((data: any) => {
          const { date_type } = filter || {};
          const dateType = dateTypeMap[date_type];
          const format =
            dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';

          const {
            list,
            timeline,
            new_start_date,
            new_end_date,
            old_start_date,
            old_end_date,
            timeline_of_start_time,
            timeline_of_end_time,
          } = data;
          const keys = list.map((val) => val.name);
          const dateArr = getTimeSpaceArray(
            timeline_of_start_time,
            timeline_of_end_time,
            dateType,
            format,
          );
          const chartData: {
            [key: string]: {
              name: string;
              time: string;
              value: number;
            }[];
          } = keys.reduce((acc, name) => {
            acc[name] = dateArr.map((date) => {
              const mention = timeline.find(
                (val) =>
                  val.topic === name &&
                  moment(val.time).format(format) === date,
              )?.mentions;

              return { name, time: date, value: mention ?? 0 };
            });
            return acc;
          }, {});

          const records =
            list?.map((val) => {
              const { name, mentions, old_mentions, rising } = val;
              const yAxisData = chartData?.[val.name] || [];

              return {
                ...val,
                rising: rising * 100,
                xAxisData: dateArr,
                yAxisData,
                newTime: [new_start_date, new_end_date],
                oldTime: [old_start_date, old_end_date],
              };
            }) || [];

          setRecords(records);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestTopic();
  }, [filter]);

  return { data: records, loading, error, refetch: requestTopic };
}

export function useKeyOpinionsRisingKeyword({
  filter,
  formatData,
  extraData,
}: {
  filter: any;
  formatData?: any;
  extraData?: any;
}) {
  const { goPage } = useDrilldownParams();
  const [records, setRecords] = useState<{
    list: Array<any>;
    new_start_date?: string;
    new_end_date?: string;
    old_start_date?: string;
    old_end_date?: string;
  }>({ list: [] });
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  function requestKeyword() {
    if (!filter?.edition_unified_id) return;
    setRecords({ list: [] });
    setError(false);
    const urlParams =
      Object.keys(extraData || {}).length !== 0 ? objToUrl(extraData) : '';
    wrapperRequest((cancelFunc) =>
      getKeyOpinionsRisingKeyword(filter, cancelFunc, urlParams)
        .then((data: any) => {
          const { bad, medium } = sentiment_frontier;
          const formatRecords = formatData?.(data, filter) || data;

          const {
            list,
            new_start_date,
            new_end_date,
            old_start_date,
            old_end_date,
          } = formatRecords;
          formatRecords.list = list.map((val) => {
            let type = '';
            let sentiment = 3;
            const { sentiment_rating, count, old_count } = val;
            if (sentiment_rating < bad) {
              type = '--negative';
              sentiment = 1;
            }
            if (sentiment_rating > medium) {
              type = '--positive';
              sentiment = 5;
            }
            const ratio = getRationPercentValue(count, old_count);
            return {
              ...val,
              type,
              newTime: [new_start_date, new_end_date],
              oldTime: [old_start_date, old_end_date],
              ratio,
              sentiment,
            };
          });

          setRecords(formatRecords);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestKeyword();
  }, [filter]);

  const { formatDate, text } = useMemo(() => {
    if (!filter) {
      return {
        formatDate: formatDateFunc('daily'),
        text: '',
      };
    }

    const { text } = getFilterDateQOQText(filter);
    let dateType = filter.date_type;
    if (
      filter.date_type === 'daily' &&
      moment(filter.start_time).isSame(moment(), 'day') &&
      moment(filter.end_time).isSame(moment(), 'day')
    ) {
      dateType = 'hourly';
    }

    return {
      formatDate: formatDateFunc(dateType),
      text,
    };
  }, [filter]);

  const keywordClick = (item: any) => {
    const { child, newTime } = item || [];
    const keywordInfo = child?.map((info: any) => info.keyword);

    const params: any = {
      ...filter,
      statistics_field: 'keyword',
      keyword: keywordInfo || [],
      topic: [],
      sentiment: [],
    };

    if (newTime && newTime.length === 2) {
      params.start_time = newTime?.[0] || filter.start_time;
      params.end_time = newTime?.[1] || filter.end_time;
    }

    // Stats Detail 为文档标注的目标页面，走服务端缓存（Server 模式）
    // 完整参数（filter/statsDetailType）存放在 goPage 的 filter 字段中，目标页读取时需解包
    goPage({
      path: '/opinion/Stats/Detail',
      filter: {
        filter: params,
        statsDetailType: 'KeyOpinions-keyword',
      },
      mode: EDrilldownMode.Server,
    });
  };

  return {
    data: records,
    loading,
    error,
    refetch: requestKeyword,
    formatDate,
    keywordClick,
    text,
  };
}

export function useKeyOpinionsSummary({
  filter,
  formatData,
  trigger,
  total = 0,
  positivePercent = 0,
  negativePercent = 0,
  neutralPercent = 0,
  positiveTotal = 0,
  neutralTotal = 0,
  negativeTotal = 0,
}: {
  filter: any;
  formatData?: any;
  trigger: boolean;
  total: number;
  positivePercent: number;
  negativePercent: number;
  neutralPercent: number;
  positiveTotal: number;
  neutralTotal: number;
  negativeTotal: number;
}) {
  const [defaultGPT, setDefaultGPT] = useState({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  const replacements: {
    [key: string]: string | number;
  } = useMemo(
    () => ({
      TOTAL_NUM: reviewsFormat(total),
      POS_RATIO: percentageFormat(positivePercent),
      NEU_RATIO: percentageFormat(neutralPercent),
      NEG_RATIO: percentageFormat(negativePercent),
      TOTAL_POS: reviewsFormat(positiveTotal),
      TOTAL_NEU: reviewsFormat(neutralTotal),
      TOTAL_NEG: reviewsFormat(negativeTotal),
      POS_NUM: reviewsFormat(positiveTotal),
      NEU_NUM: reviewsFormat(neutralTotal),
      NEG_NUM: reviewsFormat(negativeTotal),
    }),
    [
      negativePercent,
      negativeTotal,
      neutralPercent,
      neutralTotal,
      positivePercent,
      positiveTotal,
      total,
    ],
  );

  const replaceText = useCallback(
    (text: string) => {
      let replacedText = text;
      Object.keys(replacements).forEach((key) => {
        const placeholder = `$${key}`;
        replacedText = replacedText.replaceAll(
          placeholder,
          String(replacements[key]),
        );
      });
      return replacedText;
    },
    [replacements],
  );

  function requestSummary() {
    if (!filter?.edition_unified_id) return;
    setError(false);
    wrapperRequest((cancelFunc) =>
      getKeyOpinionHighGameGPTReport(filter, cancelFunc)
        .then((data: { report_en: string; report_cn: string }) => {
          const keys: Array<keyof typeof data> = ['report_en', 'report_cn'];
          keys.forEach((key) => {
            data[key] = data[key]?.length < 20 ? '' : replaceText(data[key]);
          });
          setDefaultGPT(data);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    if (trigger) {
      requestSummary();
    } else {
      setDefaultGPT({});
    }
  }, [filter, trigger, replaceText]);

  return {
    defaultGPT,
    loading,
    error,
    refetch: requestSummary,
    setDefaultGPT,
  };
}
